import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo } from 'react';
import type { ThunkDispatch } from '@reduxjs/toolkit';
import type { Request, Response } from 'express';
import type { NextPageContext } from 'next';
import Head from 'next/head';
import Script from 'next/script';
import { Provider as ReduxProvider } from 'react-redux';
import queueTask from 'post-task';
import { AdvScripts, LiveRampScript } from '@sbt-web/adv';
import { getPublicUser } from '@sbt-web/auth';
import { Category, getCategoryByFriendly } from '@sbt-web/category-utils';
import { initDidomi } from '@sbt-web/didomi';
import { defaultSocialMetaValues, socialMeta } from '@sbt-web/head-properties';
import { CategoryID, HTTPStatusCode, type Faq, type SeoMetaPayload } from '@sbt-web/network/types';
import { applyContextToFunc } from '@sbt-web/request-id-handler';
import { dispatchEventForClients, isBrowser, onDOMContentLoaded } from '@sbt-web/utils';
import { DataLayerBridge } from '@/client/bridges/DataLayer';
import updateSearchData from '@/client/bridges/SearchData';
import { LiraRunAdvScriptWrapperForListing } from '@/client/components/Adv/Lira/LiraRunAdvScriptWrapper/LiraRunAdvScriptWrapperForListing';
import { useLiraLazyInterstitialForDesktop } from '@/client/components/Adv/Lira/useLiraLazyInterstitialForDesktop';
import ListingContainer from '@/client/components/Search/Containers/ListingContainer';
import { ListingLinks } from '@/client/components/SEO/InternalLinks/types';
import { useAdvToggle } from '@/client/hooks/useAdvToggle';
import { initPresencePing } from '@/client/utilities/presence-ping';
import { updateFiltersConfigRequest, updateFiltersConfigSuccess, updateFiltersWithBadgeNew } from '@/reducers/filtersConfig';
import { getStore, type RootState } from '@/reducers/index';
import { updateItems } from '@/reducers/items';
import { startUpdating, stopUpdating, updateAdType, updateCategory, updateOrder, updateQSO, updateQuery, updateShippableOnly, updateUrgent } from '@/reducers/search';
import { LISTING_PAGE_LIMIT } from '@/shared/constants';
import { getFiltersWithNewBadge } from '@/shared/helpers/getFiltersWithNewBadge';
import { getCategoryThemeById, Theme } from '@/shared/helpers/Themes';
import { fetchFaqResponseHandler, geogosResponseHandler, getListingLinksResponseHandler, getSeoDataResponseHandler, updatePageNumber } from '@/shared/listinginitialProps';
import type { ListingQuery } from '@/shared/routes';
import listingSchema from '@/shared/tools/seo/listing';
import { hasFatalErrors, hasTimeoutErrors } from '@/tools/errorHelpers';
import { getFiltersStore } from '@/tools/search/getFiltersStore';
import { ListingHelper } from '@/tools/search/ListingHelper';
import { ImgObjSchemaListing } from '@/tools/seo/ImgObjSchema/Listing';
import { mapListingQueryToSeoRequest } from '@/tools/seo/utils';
import { sendEvent as trackListingPageView } from '@/tools/tracking/AdSearchTracking';
import { getOrCreatePulse } from '@/tools/tracking/utils';
import { getDeviceType } from '@/shared/helpers/DeviceDetector';
import { setDeviceType } from '@/reducers/system';
import { extractRequestId, isBotifyUserAgent } from '../server/detail-requests/plexus';
import '@sbt-web/adv/dist/cjs/index.css';
import { BasePageProps, setStatusOnServer } from '../types/basePageProps';
export interface ListingContext extends NextPageContext {
  query: ListingQuery;
  req: Request;
  res?: Response;
}
interface ListingProps {
  seo?: SeoMetaPayload;
  internalLinks: ListingLinks | null;
  faqData: Faq | null;
  category?: Category;
  initialState: RootState;
}
export type ListingPageProps = ListingProps & BasePageProps;
const initModuleManager = () => {
  if (window.subito.dataLayer?.search && window.subito.dataLayer?.ads) {
    void trackListingPageView();
  }
  window.subito.dataLayer?.subscribe?.('ads', () => {
    updateSearchData();
    dispatchEventForClients('subito:listing:reloaded', null, ['gtm']);
    trackListingPageView();
  });
};
export default function Listing(props) {
  const $ = _c(67);
  const handleItemClick = useLiraLazyInterstitialForDesktop();
  const {
    noAdvToggle,
    optimizelyReady
  } = useAdvToggle();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  useEffect(_temp2, t0);
  const {
    seo,
    faqData,
    internalLinks,
    initialState: t1
  } = props;
  const {
    items,
    search
  } = t1;
  let t2;
  if ($[1] !== items?.list) {
    t2 = items?.list.map(_temp3) ?? [];
    $[1] = items?.list;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const adItems = t2;
  const t3 = seo?.title ?? "Subito.it";
  let t4;
  if ($[3] !== t3) {
    t4 = <title key="page-title">{t3}</title>;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== seo) {
    t5 = seo !== undefined ? <meta name="description" key="description" content={seo.description} /> : null;
    $[5] = seo;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = items?.total === 0 || seo === undefined ? "noindex,nofollow" : seo.metaRobots;
  let t7;
  if ($[7] !== t6) {
    t7 = <meta name="robots" key="robots" content={t6} />;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== seo) {
    t8 = seo !== undefined ? <link rel="canonical" key="canonical-url" href={seo.canonicalUrl} /> : null;
    $[9] = seo;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== adItems || $[12] !== items?.total || $[13] !== search.category || $[14] !== search.filtersLabels || $[15] !== search.page || $[16] !== search.query || $[17] !== seo) {
    t9 = seo !== undefined ? <script key="listing-schema" id="listing-schema" {...listingSchema({
      items: adItems,
      description: seo.description,
      category: search.category,
      searchQuery: search.query,
      brandName: search.filtersLabels.cb || search.filtersLabels.bb,
      modelName: search.filtersLabels.cm,
      page: search.page,
      itemsCount: items?.total ?? 0
    })} /> : null;
    $[11] = adItems;
    $[12] = items?.total;
    $[13] = search.category;
    $[14] = search.filtersLabels;
    $[15] = search.page;
    $[16] = search.query;
    $[17] = seo;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== adItems) {
    t10 = <ImgObjSchemaListing adItems={adItems} />;
    $[19] = adItems;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const t11 = seo?.description;
  let t12;
  if ($[21] !== t11) {
    t12 = socialMeta({
      ...defaultSocialMetaValues,
      description: t11
    });
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== search.category.id) {
    t13 = search.category.id === CategoryID.Auto && <meta name="google-site-verification" content="dz8bU3soSfObHF0ziAEj5Y4QZ0gVKqsxdOB95KHV7Q0" />;
    $[23] = search.category.id;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t10 || $[26] !== t12 || $[27] !== t13 || $[28] !== t4 || $[29] !== t5 || $[30] !== t7 || $[31] !== t8 || $[32] !== t9) {
    t14 = <Head>{t4}{t5}{t7}{t8}{t9}{t10}{t12}{t13}</Head>;
    $[25] = t10;
    $[26] = t12;
    $[27] = t13;
    $[28] = t4;
    $[29] = t5;
    $[30] = t7;
    $[31] = t8;
    $[32] = t9;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  let t16;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <AdvScripts Script={Script} config={{
      includeOw: false,
      includeTam: false,
      includeAdSense: true,
      includePrebid: true,
      publicPath: "/static/script"
    }} />;
    t16 = <LiveRampScript Script={Script} />;
    $[34] = t15;
    $[35] = t16;
  } else {
    t15 = $[34];
    t16 = $[35];
  }
  let t17;
  if ($[36] !== adItems || $[37] !== noAdvToggle || $[38] !== optimizelyReady || $[39] !== search.adType || $[40] !== search.category || $[41] !== search.filtersDialogOpen || $[42] !== search.filtersLabels || $[43] !== search.geo || $[44] !== search.includeShippableOnly || $[45] !== search.page || $[46] !== search.query || $[47] !== search.radiusDialogOpen || $[48] !== search.radiusSearch) {
    t17 = optimizelyReady && noAdvToggle !== "B" && <LiraRunAdvScriptWrapperForListing category={search.category} query={search.query} pageIndex={search.page} adType={search.adType} geo={search.geo} filtersLabels={search.filtersLabels} radiusSearch={search.radiusSearch ?? undefined} includeShippableOnly={search.includeShippableOnly} isModalOpened={search.filtersDialogOpen ?? search.radiusDialogOpen} adItems={adItems} />;
    $[36] = adItems;
    $[37] = noAdvToggle;
    $[38] = optimizelyReady;
    $[39] = search.adType;
    $[40] = search.category;
    $[41] = search.filtersDialogOpen;
    $[42] = search.filtersLabels;
    $[43] = search.geo;
    $[44] = search.includeShippableOnly;
    $[45] = search.page;
    $[46] = search.query;
    $[47] = search.radiusDialogOpen;
    $[48] = search.radiusSearch;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  const t18 = props.initialState;
  let t19;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = isBrowser();
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  let t20;
  if ($[51] !== props.initialState) {
    t20 = getStore(t18, !t19);
    $[51] = props.initialState;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  let t21;
  if ($[53] !== faqData || $[54] !== handleItemClick || $[55] !== internalLinks || $[56] !== noAdvToggle || $[57] !== seo) {
    t21 = <ListingContainer seo={seo} internalLinks={internalLinks} faqData={faqData} handleItemClick={handleItemClick} noAdvToggle={noAdvToggle} />;
    $[53] = faqData;
    $[54] = handleItemClick;
    $[55] = internalLinks;
    $[56] = noAdvToggle;
    $[57] = seo;
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  let t22;
  if ($[59] !== t20 || $[60] !== t21) {
    t22 = <ReduxProvider store={t20}>{t21}</ReduxProvider>;
    $[59] = t20;
    $[60] = t21;
    $[61] = t22;
  } else {
    t22 = $[61];
  }
  let t23;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <Script strategy="afterInteractive" id="appsflyer-script" src="/static/script/appsflyer.js" />;
    $[62] = t23;
  } else {
    t23 = $[62];
  }
  let t24;
  if ($[63] !== t14 || $[64] !== t17 || $[65] !== t22) {
    t24 = <>{t14}{t15}{t16}{t17}{t22}{t23}</>;
    $[63] = t14;
    $[64] = t17;
    $[65] = t22;
    $[66] = t24;
  } else {
    t24 = $[66];
  }
  return t24;
}
function _temp3(i) {
  return i.item;
}
function _temp2() {
  initDidomi(getOrCreatePulse(), "listing");
  onDOMContentLoaded(initModuleManager);
  new DataLayerBridge("listing").register(getPublicUser());
  updateSearchData();
  queueTask(_temp, "user-visible");
}
function _temp() {
  initPresencePing(true);
}
const getInitialProps = async ({
  req,
  query,
  res
}: ListingContext): Promise<ListingPageProps> => {
  const store = getStore(undefined, process.env.NEXT_RUNTIME === 'nodejs');
  const Helper = new ListingHelper(store, query);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch: ThunkDispatch<RootState, void, any> = store.dispatch;
  const category = getCategoryByFriendly(query.category);
  const adType = Helper.getAdTypeForCategory(category);

  // When requesting a page over the limit, a 404 should be thrown
  // We should not allow the user to request a page over the limit,
  // this causes errors in the backend.
  // If the adType is null a 404 should be thrown
  if (Number.parseInt(query.o as string) > LISTING_PAGE_LIMIT || !adType) {
    return {
      ...setStatusOnServer(HTTPStatusCode.NotFound, res),
      pageName: 'listing',
      theme: Theme.Red,
      internalLinks: null,
      faqData: null,
      initialState: store.getState()
    };
  }
  const {
    search
  } = store.getState();
  const deviceType = getDeviceType(req);
  if (deviceType) dispatch(setDeviceType(deviceType));

  // Here we need to check if we really have to dispatch something
  // Because, even if this code is executed, it doesn't mean that everything
  // in the state should be changed according to the URL
  // This is not done in every case because some of the Actions do a check.
  dispatch(startUpdating());
  dispatch(updateCategory(category));
  dispatch(updateAdType(adType));
  dispatch(updateQuery(query.q));
  dispatch(updateFiltersConfigRequest());
  const [currentConfig, filterBadgesList] = await Promise.all([getFiltersStore(category.id, adType), getFiltersWithNewBadge()]);
  dispatch(updateFiltersConfigSuccess(currentConfig));
  if (filterBadgesList.length > 0) {
    dispatch(updateFiltersWithBadgeNew(filterBadgesList));
  }
  Helper.checkThenDispatch(search.qso !== !!query.qso, updateQSO(query.qso === 'true'));
  Helper.checkThenDispatch(search.includeShippableOnly !== !!query.shp, updateShippableOnly(query.shp === 'true'));
  Helper.checkThenDispatch(search.includeUrgent !== !!query.urg, updateUrgent(query.urg === 'true'));
  dispatch(updateOrder(query.order ?? category.orders[0]));
  if (currentConfig) {
    currentConfig.sparseFilters.forEach(filter => Helper.checkDynamicFilterThenDispatch(filter, search));
  }
  const isLocationOk = await geogosResponseHandler(Helper, search, res);
  if (!isLocationOk) {
    return {
      ...setStatusOnServer(HTTPStatusCode.NotFound, res),
      pageName: 'listing',
      faqData: null,
      theme: Theme.Red,
      internalLinks: null,
      initialState: store.getState()
    };
  }

  // Updating the page should always be kept at the end of this process
  // Because otherwise dispatching any other search param update will
  // reset its value to 1
  updatePageNumber(query, Helper, search);
  dispatch(stopUpdating());

  // Get updated searchState that will be mapped to SeoRequest.
  // TODO: investigate possible implications. The await does not actually do anything
  await dispatch(updateItems(res));
  const {
    search: searchState,
    filtersConfig: filtersConfigState
  } = store.getState();
  const metiSeoRequest = mapListingQueryToSeoRequest(query, searchState);
  const metiRequest: Promise<SeoMetaPayload> = getSeoDataResponseHandler(searchState, filtersConfigState, metiSeoRequest, res);
  const internalLinksRequest = getListingLinksResponseHandler(metiRequest, searchState, res, isBotifyUserAgent(req), extractRequestId(req));
  const pitoSeoRequest = mapListingQueryToSeoRequest(query, searchState, 'values');
  const pitoRequest = fetchFaqResponseHandler(pitoSeoRequest, req, res);
  const [seoData, internalLinks, faqData]: [SeoMetaPayload, ListingLinks | null, Faq | null] = await Promise.all([metiRequest, internalLinksRequest, pitoRequest]);
  if (hasFatalErrors(res)) {
    return {
      ...setStatusOnServer(hasTimeoutErrors(res) ? HTTPStatusCode.GatewayTimeout : HTTPStatusCode.BadRequest, res),
      pageName: 'listing',
      theme: Theme.Red,
      internalLinks: null,
      faqData: null,
      initialState: store.getState()
    };
  }
  const initialState = store.getState();
  return {
    ...setStatusOnServer(HTTPStatusCode.OK, res),
    pageName: 'listing',
    theme: getCategoryThemeById(category.id),
    seo: seoData,
    internalLinks,
    faqData,
    category,
    initialState
  };
};
Listing.getInitialProps = applyContextToFunc(getInitialProps);